/**
 * Mr. Sparks's Parakeet — the Phase 3 mid-boss. All mechanics derive from
 * persisted state (`combatRound`, `hp.monster`), so a reload mid-fight
 * resumes exactly and nothing needs a parallel bookkeeping store.
 *
 * Mechanics (enforced by monsters/boss.test.ts):
 *   - deterministic player crit every CRIT_EVERY-th round, ×CRIT_MULTIPLIER
 *     damage, with an operator quote cycling deterministically (no rng);
 *   - phase 2 at ≤50% max HP: +PHASE2_BONUS_DAMAGE incoming damage applied
 *     AFTER normal defence treatment (persists into phase 3);
 *   - phase 3 at ≤20% max HP: combat interval × PHASE3_INTERVAL_MULT;
 *   - each phase is announced exactly once (on the crossing tick);
 *   - boss fights never progress offline (see offline/summary.ts).
 */
import type { MonsterId } from '../state';

export const BOSS_MONSTER_ID: MonsterId = 'mr_sparks_parakeet';

export const CRIT_EVERY = 4;
export const CRIT_MULTIPLIER = 2;
/** Cycled deterministically: round 4 → [0], round 8 → [1], … then wraps. */
export const CRIT_QUOTES = ['Ship it.', 'Job table green.', 'Verify, then claim.'] as const;

export const PHASE2_MAX_HP_FRAC = 0.5;
export const PHASE3_MAX_HP_FRAC = 0.2;
export const PHASE2_BONUS_DAMAGE = 4;
export const PHASE3_INTERVAL_MULT = 0.5;

export type BossPhase = 1 | 2 | 3;

export function bossPhase(monsterHp: number, maxHp: number): BossPhase {
  if (monsterHp <= maxHp * PHASE3_MAX_HP_FRAC) return 3;
  if (monsterHp <= maxHp * PHASE2_MAX_HP_FRAC) return 2;
  return 1;
}

export function isCritRound(round: number): boolean {
  return round > 0 && round % CRIT_EVERY === 0;
}

/** Quote for a crit round (round must satisfy isCritRound). */
export function critQuote(round: number): string {
  const idx = (round / CRIT_EVERY - 1) % CRIT_QUOTES.length;
  return CRIT_QUOTES[idx]!;
}

export const PHASE_LABELS: Record<BossPhase, string> = {
  1: 'Phase 1 — Perch. “Ship it.” Calm. Too calm.',
  2: 'Phase 2 — Chorus. SHIP IT. SHIP IT. Volume is not a merge strategy.',
  3: 'Phase 3 — Loopstorm. Interval halved. Breathe. Do not force-push.',
};
